import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PoMenuItem, PoToolbarAction, PoToolbarProfile } from '@po-ui/ng-components';
import {
  AtsLibErrorMetricsService,
  AtsLibUsageMetricsService,
  MenuService,
  RedirectStorageService,
  ThemeStateService,
} from 'ats-ui-lib';
import { combineLatest, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UsageMetricsService } from '@services/metrics/usage-metrics.service';
import { environment } from '../../../environments/environment';
import { InviteOrigin } from '../../models/invite.model';
import { AuthService } from '../../root/auth/auth.service';
import { DatadogRumService } from '../../root/datadog-rum-service/datadog-rum.service';
import { InviteRequestService } from '../../root/invite-request/invite-request.service';
import { ApplicationMenu } from '../../root/menu/menu-items';
import { RegisterUserService } from '../../root/register-user/register-user.service';
import { AgreeTermsComponent } from '../../shared/agree-terms/agree-terms.component';

@Component({
  selector: 'ats-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.css'],
  standalone: false,
})
export class MainComponent implements OnInit {
  appAccessMetricEnabled = environment.metrics.appAccessMetric.enabled;

  readonly menu$ = this.menuService.menu$ as Observable<PoMenuItem[]>;

  initialized = false;

  currentTheme$ = this.themeService.currentTheme$;

  menuStatus = { collapsed: false, automaticToogle: true };

  @ViewChild(AgreeTermsComponent) termsConditionsMdl!: AgreeTermsComponent;

  readonly profile$ = this.authService.state$.pipe(
    map((user) => {
      if (user == null) {
        return null;
      }
      const profile: PoToolbarProfile = {
        title: user.name,
      };
      return profile;
    }),
  );

  readonly profileActions$ = combineLatest([
    this.translate.stream(['l-sign-out', 'l-settings-title']),
  ]).pipe(
    map(([translations]) => {
      const actions: PoToolbarAction[] = [
        {
          label: translations['l-settings-title'],
          url: `/main/settings`,
          icon: 'an an-gear-six',
        },
        {
          label: translations['l-sign-out'],
          action: async () => {
            await this.authService.logOut();
          },
          icon: 'an an-sign-out',
        },
      ];
      return actions;
    }),
  );

  constructor(
    private readonly menuService: MenuService,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
    private readonly window: Window,
    private readonly redirectStorage: RedirectStorageService,
    private readonly themeService: ThemeStateService,
    private readonly router: Router,
    private readonly registerUserService: RegisterUserService,
    private readonly atsLibUsageMetricsService: AtsLibUsageMetricsService,
    private readonly datadogRunService: DatadogRumService,
    private readonly inviteRequestService: InviteRequestService,
    private readonly usageMetricsService: UsageMetricsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly errorMetricsService: AtsLibErrorMetricsService,
  ) {}

  ngOnInit(): void {
    void this.onInitAsync();
  }

  async onInitAsync(): Promise<void> {
    await this.authService.init();
    if (this.authService.snapshot == null) {
      await this.handleInitializeNotLoggedUser();
    } else {
      await this.handleInitializeLoggedUser();
    }

    this.initialized = true;
  }

  private async handleInitializeNotLoggedUser(): Promise<void> {
    await this.createReferrerOriginUsageMetric();
    this.handleApplicantInvite();
    const id = this.redirectStorage.setRedirectRoute(this.window.location.href);
    await this.router.navigate(['register-user', id], {
      queryParams: {
        doNotValidate: true,
      },
    });
  }

  private async handleInitializeLoggedUser(): Promise<void> {
    const loggedUser = this.authService.snapshot!;
    if (!(await this.registerUserService.createUser(loggedUser))) {
      await this.authService.logOut();
      return;
    }
    this.datadogRunService.setUserInformation(this.authService.snapshot!);
    if (this.appAccessMetricEnabled) {
      this.atsLibUsageMetricsService.sendAppAccessUsageMetric();
    }
    this.errorMetricsService.init();
    this.menuService.init(ApplicationMenu);
    this.initialized = true;
    await this.checkAgreeTerms();
  }

  async checkAgreeTerms(): Promise<void> {
    const termsUse = await lastValueFrom(this.registerUserService.getAgreeTerms());
    if (!termsUse.agreeTerms)
      this.termsConditionsMdl.openModal(this.authService.snapshot!.termsUseCurrentVersion);
  }

  private handleApplicantInvite(): void {
    const urlStr = this.window.location.href;

    if (urlStr?.includes('join-job-opportunity'))
      this.inviteRequestService.saveHashInvite(InviteOrigin.JobOpportunity);
    else if (urlStr?.includes('join-talent-bank'))
      this.inviteRequestService.saveHashInvite(InviteOrigin.TalentBank);
  }

  private async createReferrerOriginUsageMetric(): Promise<void> {
    if (this.authService.isLeaving()) {
      return;
    }
    const tenantId = this.activatedRoute.snapshot.queryParams.tenantId;
    const referrerOrigin = document.referrer;
    await this.usageMetricsService.sendReferrerOriginUsageMetric(referrerOrigin, tenantId);
  }
}
